import React, { useEffect, useState } from 'react';
import { subscribeTrackKey } from '../../../../services/TrackKeysService';
import { MODES, NAMES, keyFromDoc, keyName, transposeKey } from '../../../../services/musicKeyModel';
import { keyDocFor } from './sectionMarkers';
import './SongKeyLine.css';

/**
 * The song's key on the player (2026-09-29). Anthony: "Do we have a system
 * that identifies the key that the song is in?" -> "Let's just build the key
 * detection today".
 *
 * One line: "Key G major · detected", plus "could be E minor" when the
 * estimate barely beat its runner-up (a key and its relative minor share
 * every note), plus "playing in A major" when a saved key or the live key
 * change moves it. A teacher's correction wins and reads "set by a teacher".
 * **Change** picks tonic + major/minor; **Find the key** queues the helper
 * on a track nobody has checked yet. Always rendered for the tab's own audio
 * track (keratoconus, and no hover on the PWA). Staff-only data: the family
 * page never reads trackKeys.
 */
const SongKeyLine = ({ file, files = [], player, jobs = [], onFind = null, onConfirm = null }) => {
	const { id, semitones: fileShift } = keyDocFor(file, files);
	const [docData, setDocData] = useState(undefined); // undefined = loading
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState({ tonic: 'C', mode: 'major' });
	const [error, setError] = useState('');

	useEffect(() => {
		setDocData(undefined);
		setEditing(false);
		if (!id) return undefined;
		return subscribeTrackKey(id, setDocData, () => setDocData(null));
	}, [id]);

	if (!file || !id || docData === undefined) return null;
	const { key, source, alt, unsure } = keyFromDoc(docData);
	const liveShift = player && player.current && player.current.file === file ? Number(player.semitones) || 0 : 0;
	const shift = fileShift + liveShift;
	const playing = key && shift ? transposeKey(key, shift) : null;
	const pending = (jobs || []).find((j) => j && j.kind === 'key' && j.keyDocId === id && (j.status === 'queued' || j.status === 'claimed'));

	const run = async (fn) => {
		setError('');
		try {
			await fn();
			setEditing(false);
		} catch (e) {
			setError(`That did not save. Please tell Anthony right away. (${e && e.message ? e.message : 'unknown error'})`);
		}
	};
	const startEdit = () => {
		setDraft(key || { tonic: 'C', mode: 'major' });
		setEditing(true);
	};

	return (
		<div className="ld-songkey" role="group" aria-label="Song key">
			<span className="ld-songkey__label">
				<i className="fa-sharp fa-solid fa-music" aria-hidden="true" />
				Key
			</span>
			{key ? (
				<>
					<span className="ld-songkey__value">{keyName(key)}</span>
					<span className="ld-songkey__meta">
						{source === 'teacher' ? 'set by a teacher' : 'detected'}
						{source === 'detected' && unsure && alt ? ` · could be ${keyName(alt)}` : ''}
					</span>
					{playing && <span className="ld-songkey__playing">· playing in {keyName(playing)}</span>}
				</>
			) : (
				<span className="ld-songkey__meta">{pending ? 'Finding the key…' : 'Not found yet'}</span>
			)}
			{!editing && !key && !pending && onFind && (
				<button type="button" className="ld-songkey__btn" onClick={() => run(() => onFind(file, id))}>
					Find the key
				</button>
			)}
			{!editing && onConfirm && (
				<button type="button" className="ld-songkey__btn" onClick={startEdit}>
					{key ? 'Change' : 'Set it'}
				</button>
			)}
			{editing && (
				<span className="ld-songkey__edit">
					<select aria-label="Key note" value={draft.tonic} onChange={(e) => setDraft({ ...draft, tonic: e.target.value })}>
						{NAMES.map((n) => (
							<option key={n} value={n}>
								{n}
							</option>
						))}
					</select>
					<select aria-label="Major or minor" value={draft.mode} onChange={(e) => setDraft({ ...draft, mode: e.target.value })}>
						{MODES.map((m) => (
							<option key={m} value={m}>
								{m}
							</option>
						))}
					</select>
					<button type="button" className="ld-songkey__btn ld-songkey__btn--primary" onClick={() => run(() => onConfirm(id, draft))}>
						Save
					</button>
					{source === 'teacher' && (
						<button type="button" className="ld-songkey__btn" onClick={() => run(() => onConfirm(id, null))}>
							Use the detected key
						</button>
					)}
					<button type="button" className="ld-songkey__btn" onClick={() => setEditing(false)}>
						Cancel
					</button>
				</span>
			)}
			{error && (
				<div className="ld-dismissable-error" role="alert">
					<p>{error}</p>
					<button type="button" className="ld-dismiss" onClick={() => setError('')}>
						Dismiss
					</button>
				</div>
			)}
		</div>
	);
};

export default SongKeyLine;
